import * as React from "react";

import { cn } from "../../lib/utils";

/**
 * Ring geometry, in the 24-unit viewBox.
 *
 * The stroke is ~9% of the DIAMETER. That ratio is the whole character of this spinner: heavier and it reads as
 * a chunky donut, lighter and it disappears at 20px. Clerk's own spinner is not inspectable — `clerk-js` is
 * fetched from their CDN rather than installed — so this was matched by eye against their rendered ring, and
 * the first attempt at 12.5% was visibly too thick beside it.
 */
const RADIUS = 10.1;
const STROKE_WIDTH = 1.8;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** Arc sweep, ~100 degrees. Enough to read as motion, short enough not to look like a progress value. */
const ARC_FRACTION = 0.28;

export interface SpinnerProps extends React.SVGProps<SVGSVGElement> {
  /** Accessible label. Omit inside an element that already announces the loading state. */
  label?: string;
}

/**
 * The app's loading spinner: a full ring track with one accent arc travelling around it.
 *
 * Replaces lucide's `Loader2Icon`, which draws only the arc — with no track it reads as a fragment rotating in
 * empty space, and at small sizes on a dark surface it is easy to miss entirely. The track gives the shape a
 * constant silhouette, so the eye locks onto the ring and only the colour moves. This is the same construction
 * Clerk's spinner uses, which matters here because Clerk renders its own loading state during sign-in — two
 * different spinners inside one flow reads as two different apps.
 *
 * Round line caps and a fixed 24-unit viewBox mean it scales by font/size utilities without the stroke
 * thinning out.
 */
export const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(function Spinner(
  { className, label, ...props },
  ref,
) {
  return (
    <svg
      ref={ref}
      viewBox='0 0 24 24'
      fill='none'
      className={cn("size-5 animate-spin", className)}
      role={label ? "status" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...props}
    >
      {/* Track: the constant silhouette. `currentColor` at low opacity so it inherits whatever colour the
          caller sets, rather than pinning a grey that disappears on some surfaces. */}
      <circle cx='12' cy='12' r={RADIUS} stroke='currentColor' strokeWidth={STROKE_WIDTH} opacity='0.18' />
      <circle
        cx='12'
        cy='12'
        r={RADIUS}
        stroke='currentColor'
        strokeWidth={STROKE_WIDTH}
        strokeLinecap='round'
        strokeDasharray={`${CIRCUMFERENCE * ARC_FRACTION} ${CIRCUMFERENCE * (1 - ARC_FRACTION)}`}
      />
    </svg>
  );
});
